import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { getDateWithOffset, capitalize } from "common/api";
import { useDimensions } from "common/hooks";
import NVStackedAreaChartLive from "common/NVStackedAreaChartLive";
import { formatDate } from "views/Statistics/SubscriberDetail/livemetrics.utils";
import {
  getItemsUpdate,
  getHoveredTime,
  getMouseX,
  getValueItems,
  getValueExtraItems,
  Title,
  CursorLine,
  LegendContainer,
  fieldTotal
} from "./utils";
import ExtraChart from "./ExtraChart";
import MainChart from "./MainChart";

const Wrapper = styled.div`
  width: 100%;
  margin-bottom: 25px;
`;

const ExtraChartsWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  @media (max-width: 900px) {
    display: block;
  }
`;

const marginRight = 50;
const marginLeft = 70;
const marginTop = 5;
const marginBottom = 30;

const getHasRestValue=(restField, fieldsTop20)=>{
  if(!restField){
    return false
  };
  if(fieldsTop20.length===0){
    return false
  }

  return restField.total >= fieldsTop20[fieldsTop20.length - 1].total;
}

const Charts = ({
  value,
  timeOffset,
  fields,
  fieldsMap,
  title,
  onMouseEvent,
  mouseEvent,
  total,
  output,
  timeWindow,
  setLiveMode,
  display,
  type,
  subscriberGroup,
  initTime,
  seconds,
}) => {
  const [items, setItems] = useState([]);
  const [window, setWindow] = useState(0);
  const [filterFields, setFilterFields] = useState(new Set());
  const [filteredFields, setFilteredFields] = useState([]);
  const firstValue = useRef(false);
  const fieldsSet = useRef({});
  const dataMap = useRef({});
  const fieldsTop20 = useRef([]);
  const fieldsTopExtra = useRef([]);
  const [rangeMain, setRangeMain] = useState(0);
  const [rangeExtra, setRangeExtra] = useState(0);
  const [plotSelection, setPlotSelection] = useState(["total"])

  const valueKeys = ["link", "rtt", "rtx"].map((key) => `${type}key`);

  function reset() {
    setItems([]);
    setWindow(0);
    setFilterFields(new Set());
    setFilteredFields([]);
    firstValue.current = false;
    dataMap.current = {};
    fieldsSet.current = {};
    fieldsTop20.current = [];
    fieldsTopExtra.current = [];
  }

  useEffect(() => {
    reset();
  }, [output, timeWindow, subscriberGroup]);

  useEffect(() => {
    if (!value) {
      return;
    }

    const now = getDateWithOffset(timeOffset);
    const newValue = { ...value, total };

    if (!firstValue.current) {
      firstValue.current = newValue;
    }

    const fieldNames = Object.keys(fieldsMap);
    const addedFields = fieldNames.filter((field) => !fieldsSet.current[field]);
    const filterFieldsWithoutRemoved = [...filterFields].filter(
      (fieldName) => fieldsSet.current[fieldName]
    );
    setFilterFields(new Set([...filterFieldsWithoutRemoved, ...addedFields]));
    fieldsSet.current = fieldsMap;

    const timeFrame =
      newValue.time.getTime() - firstValue.current.time.getTime();
    let { itemsUpdate, window: windowNew } = getItemsUpdate({
      items,
      now,
      fields,
      timeOffset,
      timeFrame,
      timeWindow,
      newValue,
      valueKeys,
      window,
    });
    setWindow(windowNew);
    const lastValue = itemsUpdate[itemsUpdate.length - 1];
    if (
      lastValue?.time &&
      newValue?.time &&
      formatDate(lastValue.time) !== formatDate(newValue.time)
    ) {
      delete dataMap.current[formatDate(itemsUpdate[0].time)];
      dataMap.current[formatDate(newValue.time)] = {
        time: newValue.time,
        fields: [...fields],
        value: newValue,
        totalLink: total?.link,
        totalRtt: total?.rtt,
        totalRtx: total?.rtx,
      };
      setItems([...itemsUpdate.slice(1), newValue]);
    }
  }, [value]);

useEffect(() => {
  const sortedFields = Object.entries(fieldsMap)
    .map(([key, { total, color, totalUp, totalDown }]) => {
      return {
        label: key,
        name: key,
        color,
        total,
        totalUp,
        totalDown,
      };
    })
    .sort((a, b) => {
      if (type === "down") {
        return b.totalDown - a.totalDown;
      } else {
        return b.totalUp - a.totalUp;
      }
    });
    fieldsTop20.current = sortedFields.slice(0,20);
    const fieldsExtraTop = sortedFields.slice(0,9);
    const fieldsExtraTopSet = new Set(fieldsExtraTop.map((field)=> field.name));

    const leftExtraTop =
      fieldsTopExtra.current.length === 0
        ? []
        : fieldsTopExtra.current.filter(
            ({ name }) => !fieldsExtraTopSet.has(name)
          );
    const leftExtraTopSet = leftExtraTop.length === 0 ? new Set([]) :  new Set(leftExtraTop.map(({name})=> name));
    fieldsTopExtra.current = fieldsExtraTop;
    const newPlotSelection = plotSelection.filter((name)=> !leftExtraTopSet.has(name));
    setPlotSelection(newPlotSelection);
}, [JSON.stringify(fieldsMap)]);

  const hasRestValue = getHasRestValue(fieldsMap["rest"], fieldsTop20.current);

  const openDashboard = (label, output) => {
    if (label === "rest") {
      return;
    }

    setLiveMode(false);
    views.doKeep("viewSubscriberThroughput");

    const target =
      output === "ip-addresses"
        ? { addr: label, subsId: null }
        : { addr: null, subsId: label };
    globalNavigate("viewSubscriberThroughput", {
      ...target,
      returnView: "viewSubscriberThroughput",
    });
  };

  const hoverderTime = getHoveredTime(mouseEvent, items, rangeMain);
  const { mouseXMain, mouseXExtra } =
    getMouseX(mouseEvent, rangeMain, rangeExtra) || {};

  const width = rangeMain + marginLeft + marginRight;

  return (
    <Wrapper>
      <Title className="chart-title">{`${capitalize(type)}link`}</Title>
      <MainChart
        items={getValueItems(items, `${type}link`)}
        fields={fields}
        fieldsSet={filterFields}
        margin={{
          top: marginTop,
          right: marginRight,
          bottom: marginBottom,
          left: marginLeft,
        }}
        mouseEvent={mouseEvent}
        onMouseEvent={onMouseEvent}
        rangeMain={rangeMain}
        setRangeMain={setRangeMain}
        hoverderTime={hoverderTime}
        currentData={dataMap.current[formatDate(hoverderTime)]}
        width={rangeMain + marginLeft + marginRight}
        fieldsMap={fieldsMap}
        mouseXMain={mouseXMain}
        top20Fields={fieldsTop20.current}
        hasRestValue={hasRestValue}
        openDashboard={openDashboard}
        type={type}
        output={output}
        value={value}
        total={total}
        initTime={initTime}
        renderRate={window === 10 ? 2 : 1}
        seconds={seconds}
        plotSelection={plotSelection}
        setPlotSelection={setPlotSelection}
      />
      {display === "downall" || display === "upall" ? (
        <ExtraChartsWrapper>
          {["rtt", "rtx"].map((key) => {
            const { items: itemsExtra, max } = getValueExtraItems(
              items,
              key,
              type
            );
            return (
              <ExtraChart
                key={`${type}${key}`}
                type={`${type}${key}`}
                items={itemsExtra}
                margin={{
                  top: marginTop,
                  right: marginRight,
                  bottom: marginBottom,
                  left: marginLeft,
                }}
                mouseEvent={mouseEvent}
                onMouseEvent={onMouseEvent}
                rangeExtra={rangeExtra}
                rangeMain={rangeMain}
                setRangeExtra={setRangeExtra}
                hoverderTime={hoverderTime}
                currentData={dataMap.current[formatDate(hoverderTime)]}
                width={rangeExtra + marginLeft + marginRight + 40}
                fieldsMap={{ ...fieldsMap, total: fieldTotal }}
                mouseXExtra={mouseXExtra}
                openDashboard={openDashboard}
                output={output}
                renderRate={window === 10 ? 2 : 1}
                max={max}
                plotSelection={plotSelection}
              />
            );
          })}
        </ExtraChartsWrapper>
      ) : null}
    </Wrapper>
  );
};

export default Charts;
